import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AreaChart, LineChart, PieChart, TrendingUp, Cpu, ArrowDown, Play, Calculator, Brain, ChevronLeft, ChevronRight, Phone, Smartphone, PhoneCall, ListOrdered, Settings, Clock } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { ROICalculatorModal } from './ROICalculatorModal';
import VideoModal from "@/components/VideoModal";
import AudioPlayerModal from "@/components/AudioPlayerModal";

const RestaurantFeatures = () => {
  const [isROIModalOpen, setIsROIModalOpen] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [isAudioModalOpen, setIsAudioModalOpen] = useState(false);
  const [currentInsight, setCurrentInsight] = useState(0);
  const [activeProduct, setActiveProduct] = useState<'virtual-host' | 'virtual-assistant'>('virtual-host');

  // Virtual Assistant insights (existing)
  const vaInsights = [
    { image: '/images/ai-insights/ai-insight-pricing.png', alt: 'AI Price Point Analysis' },
    { image: '/images/ai-insights/ai-insight-preferences.png', alt: 'AI Customer Preferences Analysis' },
    { image: '/images/ai-insights/ai-insight-strategic.png', alt: 'AI Strategic Opportunity Analysis' }
  ];

  // Virtual Host insights (text-based for now, can add images later)
  const vhInsights = [
    {
      title: "Call Performance",
      tag: "Weekly Summary",
      text: "Your AI Host handled 127 calls this week with 99.2% accuracy. Peak hours were Tuesday and Friday 6-8 PM. Average call duration: 3.2 minutes. Consider adding more specials to boost average order value during peak times."
    },
    {
      title: "Revenue Recovery",
      tag: "Missed Call Analysis",
      text: "Before AI Host, you were missing an estimated 23 calls per week during rush hours. This week, all calls were answered within 3 rings. Recovered revenue estimate: $736 from calls that would have gone to voicemail."
    },
    {
      title: "Order Insights",
      tag: "Customer Patterns",
      text: "Top phone orders: Pizza Margherita (18%), BBQ Chicken Wings (15%), Caesar Salad (12%). Customers who order via phone have 28% higher average order value than walk-ins. AI upselling added $2.40 average per order."
    }
  ];

  const aiInsights = activeProduct === 'virtual-host' ? vhInsights : vaInsights;

  // Reset insight index when product changes
  useEffect(() => {
    setCurrentInsight(0);
  }, [activeProduct]);

  // Auto-rotate insights every 8 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentInsight((prev) => (prev + 1) % aiInsights.length);
    }, 8000);
    return () => clearInterval(timer);
  }, [aiInsights.length]);

  const nextInsight = () => setCurrentInsight((prev) => (prev + 1) % aiInsights.length);
  const prevInsight = () => setCurrentInsight((prev) => (prev - 1 + aiInsights.length) % aiInsights.length);

  // Virtual Host features
  const vhFeatures = [
    {
      icon: PhoneCall,
      title: "AI Call Minutes",
      description: "Track your AI call usage in real-time. See minutes used, remaining balance, and billing cycle progress at a glance.",
      impact: "Monitor usage instantly",
      metric: "Real-time minute tracking",
      color: "text-orange-400"
    },
    {
      icon: ListOrdered,
      title: "Live Order Queue",
      description: "View all orders in real-time: pending payments, in-progress, and completed. Never miss an order status update.",
      impact: "Manage orders effortlessly",
      metric: "Real-time order tracking",
      color: "text-blue-400"
    },
    {
      icon: Settings,
      title: "Service Controls",
      description: "Toggle order acceptance on/off, set wait times, and control which menu sections are available for phone ordering.",
      impact: "Full control anytime",
      metric: "Instant service toggles",
      color: "text-purple-400"
    },
    {
      icon: Clock,
      title: "AI Host Settings",
      description: "Customize your AI's greeting, set operating hours, configure holiday messages, and manage call forwarding rules.",
      impact: "Personalize your AI",
      metric: "Custom voice & hours",
      color: "text-dineai-yellow"
    }
  ];

  // Virtual Assistant features (existing)
  const vaFeatures = [
    {
      icon: PieChart,
      title: "AI Performance Overview",
      description: "Track your menu's AI-powered performance with weekly analytics showing orders, revenue, and customer engagement metrics.",
      impact: "Monitor revenue impact",
      metric: "Weekly performance reports",
      color: "text-emerald-400"
    },
    {
      icon: LineChart,
      title: "AI Menu Intelligence",
      description: "Discover your star performers, underperformers, and hidden gems with intelligent menu analysis and recommendations.",
      impact: "Identify profitable menu items",
      metric: "Weekly AI-powered insights",
      color: "text-blue-400"
    },
    {
      icon: AreaChart,
      title: "Customer Insights",
      description: "Understand guest favorites and trending items with detailed analytics on dietary preferences and ordering patterns.",
      impact: "Personalize experiences",
      metric: "Track dietary trends",
      color: "text-purple-400"
    },
    {
      icon: TrendingUp,
      title: "Price Sweet Spots",
      description: "Optimize your menu pricing with AI analysis that identifies the perfect price points for maximum profitability.",
      impact: "Maximize profit margins",
      metric: "Data-driven pricing insights",
      color: "text-dineai-yellow"
    }
  ];

  const features = activeProduct === 'virtual-host' ? vhFeatures : vaFeatures;

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  return (
    <section id="restaurant-features" className="py-12 bg-[#2256e5] relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-dineai-yellow/20 blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 rounded-full bg-white/10 blur-3xl"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-block bg-dineai-yellow/20 px-4 py-2 rounded-full mb-4">
            <span className="text-dineai-yellow font-medium text-sm flex items-center gap-2">
              <Cpu className="w-4 h-4" />
              POWERFUL AI FEATURES
            </span>
          </div>
          
          <h2 className="text-2xl md:text-3xl font-bold mb-3 text-white">
            The Technology Behind Your <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">Revenue Growth</span>
          </h2>
          
          <p className="text-white/80 text-base max-w-2xl mx-auto mb-6">
            {activeProduct === 'virtual-host'
              ? "Real-time dashboard to monitor calls, manage orders, and control your AI phone system."
              : "Powerful AI analytics and insights to understand your menu performance and optimize your restaurant's profitability."
            }
          </p>

          {/* Product Toggle Tabs */}
          <div className="flex justify-center">
            <div className="inline-flex bg-white/10 rounded-full p-1 backdrop-blur-sm border border-white/20">
              <button
                onClick={() => setActiveProduct('virtual-host')}
                className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 flex items-center gap-2 ${
                  activeProduct === 'virtual-host'
                    ? 'bg-dineai-yellow text-black shadow-lg'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                <Phone className="w-4 h-4" />
                AI Virtual Host
              </button>
              <button
                onClick={() => setActiveProduct('virtual-assistant')}
                className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 flex items-center gap-2 ${
                  activeProduct === 'virtual-assistant'
                    ? 'bg-dineai-yellow text-black shadow-lg'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                <Smartphone className="w-4 h-4" />
                AI Virtual Assistant
              </button>
            </div>
          </div>
        </motion.div>
        
        {/* Features Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeProduct}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {features.map((feature, index) => (
              <motion.div
                key={`${activeProduct}-${index}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="group"
              >
                <div className="backdrop-blur-sm rounded-xl p-6 border border-yellow-400/30 shadow-xl hover:shadow-2xl hover:scale-105 transition-all h-full flex flex-col" style={{ backgroundColor: '#3256dc' }}>
                  <div className="mb-4">
                    <feature.icon className={`w-10 h-10 ${feature.color}`} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-bold mb-3 text-white min-h-[3rem] flex items-start">{feature.title}</h3>
                  <p className="text-white/70 text-sm mb-4 flex-grow min-h-[4.5rem]">{feature.description}</p>
                  <div className="pt-4 border-t border-white/10">
                    <div className="text-dineai-yellow font-semibold text-sm mb-2 min-h-[1.5rem] leading-tight">{feature.impact}</div>
                    <div className="text-white/50 text-xs min-h-[1rem] leading-tight">{feature.metric}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* AI Performance Analysis Showcase */}
        <motion.div
          className="mt-16 max-w-5xl mx-auto"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {/* Insights Carousel */}
          <div className="relative">
            {/* Section Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-dineai-yellow" />
                <span className="text-white font-semibold">
                  {activeProduct === 'virtual-host' ? 'AI Host Insights' : 'AI Performance Insights'}
                </span>
              </div>
              <span className="text-white/50 text-sm">
                Insight {currentInsight + 1} of {aiInsights.length}
              </span>
            </div>

            {/* Main Carousel Container */}
            <div className="relative overflow-hidden rounded-2xl">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${activeProduct}-${currentInsight}`}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.7 }}
                  className="w-full"
                >
                  {activeProduct === 'virtual-host' ? (
                    // Text-based insight card for Virtual Host
                    <div className="bg-gradient-to-br from-[#3256dc] to-[#2256e5] rounded-2xl p-8 border border-white/20 shadow-2xl mx-16">
                      <div className="flex flex-col items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center">
                          <PhoneCall className="w-6 h-6 text-orange-400" />
                        </div>
                        <div className="text-center">
                          <h4 className="text-white font-bold text-xl">{(aiInsights[currentInsight] as any).title}</h4>
                          <span className="text-xs bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full inline-block mt-2">
                            {(aiInsights[currentInsight] as any).tag}
                          </span>
                        </div>
                      </div>
                      <p className="text-white/80 text-base leading-relaxed text-center">
                        {(aiInsights[currentInsight] as any).text}
                      </p>
                    </div>
                  ) : (
                    // Image-based insight for Virtual Assistant
                    <img
                      src={(aiInsights[currentInsight] as any).image}
                      alt={(aiInsights[currentInsight] as any).alt}
                      className="w-full rounded-2xl shadow-2xl"
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={prevInsight}
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-dineai-yellow/90 hover:bg-dineai-yellow text-black p-3 rounded-full shadow-lg transition-all hover:scale-110"
              aria-label="Previous insight"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextInsight}
              className="absolute right-0 top-1/2 -translate-y-1/2 bg-dineai-yellow/90 hover:bg-dineai-yellow text-black p-3 rounded-full shadow-lg transition-all hover:scale-110"
              aria-label="Next insight"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-6">
              {aiInsights.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentInsight(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === currentInsight
                      ? 'w-8 bg-dineai-yellow'
                      : 'w-2 bg-white/30 hover:bg-white/50'
                  }`}
                  aria-label={`Go to insight ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Description */}
          <motion.div
            className="text-center mt-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <div className="inline-flex items-center gap-2 bg-dineai-yellow/20 px-4 py-2 rounded-full mb-4">
              <Brain className="w-5 h-5 text-dineai-yellow" />
              <span className="text-dineai-yellow font-semibold">INCLUDED IN ALL PLANS</span>
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">
              {activeProduct === 'virtual-host'
                ? "Your AI Control Center Works 24/7"
                : "Your AI Data Analyst Works 24/7"
              }
            </h3>
            <p className="text-white/80 text-lg max-w-3xl mx-auto">
              {activeProduct === 'virtual-host'
                ? <>Monitor <span className="text-dineai-yellow font-semibold">real-time call metrics</span>, manage orders, and control your AI phone system from one powerful dashboard. It's like having a call center manager on your team!</>
                : <>Every week, our AI analyzes your menu performance and generates <span className="text-dineai-yellow font-semibold">3 actionable insights</span> to boost revenue, optimize pricing, and improve customer satisfaction. It's like having a data scientist on your team!</>
              }
            </p>
          </motion.div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <p className="text-white/80 text-lg mb-6">
            {activeProduct === 'virtual-host'
              ? "Ready to never miss another call?"
              : "Ready to transform your restaurant with AI?"
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {activeProduct === 'virtual-host' ? (
              <Button
                onClick={() => setIsAudioModalOpen(true)}
                className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-4 sm:py-6 px-6 sm:px-8 text-base sm:text-lg font-bold rounded-full shadow-lg transform hover:scale-105 transition-all flex items-center gap-2"
              >
                <Play className="w-4 sm:w-5 h-4 sm:h-5" />
                PLAY DEMO
              </Button>
            ) : (
              <Button
                onClick={() => setIsVideoOpen(true)}
                className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-4 sm:py-6 px-6 sm:px-8 text-base sm:text-lg font-bold rounded-full shadow-lg transform hover:scale-105 transition-all flex items-center gap-2"
              >
                <Play className="w-4 sm:w-5 h-4 sm:h-5" />
                SEE AI IN ACTION
              </Button>
            )}
            <Button
              onClick={() => setIsROIModalOpen(true)}
              className="bg-transparent border-2 border-[#43acff] text-[#43acff] hover:bg-[#43acff] hover:text-white hover:border-[#43acff] py-4 sm:py-6 px-6 sm:px-8 text-base sm:text-lg font-medium rounded-full transform hover:scale-105 transition-all flex items-center gap-2">
              <Calculator className="w-4 sm:w-5 h-4 sm:h-5" />
              CALCULATE YOUR ROI
            </Button>
          </div>
        </motion.div>

        {/* Bridge to next section */}
        <motion.div 
          className="mt-16 flex justify-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div 
            className="relative group cursor-pointer"
            onClick={() => {
              const element = document.getElementById('customer-features');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-dineai-yellow/20 via-dineai-yellow/30 to-dineai-yellow/20 blur-xl group-hover:blur-2xl transition-all duration-300"></div>
            <div className="relative bg-gradient-to-r from-dineai-yellow/10 via-dineai-yellow/20 to-dineai-yellow/10 backdrop-blur-sm border border-dineai-yellow/30 rounded-2xl px-8 py-4 group-hover:border-dineai-yellow/50 transition-all duration-300">
              <p className="text-white/90 text-base font-medium flex items-center gap-3">
                <span className="bg-gradient-to-r from-white via-dineai-yellow to-white bg-clip-text text-transparent">
                  Now let's see how your customers experience this AI-powered transformation...
                </span>
                <ArrowDown className="w-5 h-5 text-dineai-yellow animate-bounce" />
              </p>
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* ROI Calculator Modal */}
      <ROICalculatorModal
        isOpen={isROIModalOpen}
        onClose={() => setIsROIModalOpen(false)}
      />

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        videoUrl="https://youtube.com/shorts/l3xW3Uv0iGM?feature=share"
        title="Dine AI Demo - Transform Your Restaurant"
        format="mobile"
      />

      {/* Audio Player Modal for Virtual Host Demo */}
      <AudioPlayerModal
        isOpen={isAudioModalOpen}
        onClose={() => setIsAudioModalOpen(false)}
        audioSrc="/audio/virtual-host-demo.wav"
        title="AI Virtual Host Demo"
      />
    </section>
  );
};

export default RestaurantFeatures;